/**
 * Created by denis.gusarov on 3/3/2017.
 */

namespace Factive.Core {

    export class Underscore {

        // Save bytes in the minified (but not gzipped) version:
        private static ArrayProto = Array.prototype;
        private static ObjProto = Object.prototype;
        private static FuncProto = Function.prototype;

        // Create quick reference variables for speed access to core prototypes.
        private static push             = Underscore.ArrayProto.push;
        private static slice            = Underscore.ArrayProto.slice;
        private static toString         = Underscore.ObjProto.toString;
        private static hasOwnProperty   = Underscore.ObjProto.hasOwnProperty;

        // All **ECMAScript 5** native function implementations that we hope to use
        // are declared here.
        private static nativeIsArray      = Array.isArray;
        private static nativeKeys         = Object.keys;
        private static nativeBind         = Underscore.FuncProto.bind;
        private static nativeCreate       = Object.create;

        // Internal function that returns an efficient (for current engines) version
        // of the passed-in callback, to be repeatedly applied in other Underscore
        // functions.
        private static optimizeCb (func, context, argCount = null) {
            if (context === void 0) return func;
            switch (argCount == null ? 3 : argCount) {
                case 1: return function(value) {
                    return func.call(context, value);
                };
                case 2: return function(value, other) {
                    return func.call(context, value, other);
                };
                case 3: return function(value, index, collection) {
                    return func.call(context, value, index, collection);
                };
                case 4: return function(accumulator, value, index, collection) {
                    return func.call(context, accumulator, value, index, collection);
                };
            }
            return function() {
                return func.apply(context, arguments);
            };
        };

        private static identity(value) {
            return value;
        }

        private static isFunction(value) {
            // The use of `Object#toString` avoids issues with the `typeof` operator
            // in older versions of Chrome and Safari which return 'function' for regexes
            // and Safari 8 which returns 'object' for typed array constructors.
            return Underscore.isObject(value) && Object.prototype.toString.call(value) == '[object Function]';
        }

        private static isObject(value) {
            // Avoid a V8 JIT bug in Chrome 19-20.
            // See https://code.google.com/p/v8/issues/detail?id=2291 for more details.
            var type = typeof value;
            return !!value && (type == 'object' || type == 'function');
        }

        public static extendOwn: (obj, attrs)=> any = Underscore.createAssigner(Underscore.keys);

        public static extend: (obj, attrs)=> any = Underscore.createAssigner(Underscore.allKeys);

        private static createAssigner(keysFunc, undefinedOnly = null) {
            return function(obj) {
                var length = arguments.length;
                if (length < 2 || obj == null) return obj;
                for (var index = 1; index < length; index++) {
                    var source = arguments[index],
                        keys = keysFunc(source),
                        l = keys.length;
                    for (var i = 0; i < l; i++) {
                        var key = keys[i];
                        if (!undefinedOnly || obj[key] === void 0) obj[key] = source[key];
                    }
                }
                return obj;
            };
        };

        private static matcher (attrs) {
            attrs = Underscore.extendOwn({}, attrs);
            return function(obj) {
                return Underscore.isMatch(obj, attrs);
            };
        };

        private static isMatch = function(object, attrs) {
            var keys = Underscore.keys(attrs), length = keys.length;
            if (object == null) return !length;
            var obj = Object(object);
            for (var i = 0; i < length; i++) {
                var key = keys[i];
                if (attrs[key] !== obj[key] || !(key in obj)) return false;
            }
            return true;
        };

        // A mostly-internal function to generate callbacks that can be applied
        // to each element in a collection, returning the desired result — either
        // identity, an arbitrary callback, a property matcher, or a property accessor.
        private static cb (value, context, argCount = null) {
            if (value == null) return Underscore.identity;
            if (Underscore.isFunction(value)) return Underscore.optimizeCb(value, context, argCount);
            if (Underscore.isObject(value)) return Underscore.matcher(value);
            return Underscore.property(value);
        };

        // An internal function for creating a new object that inherits from another.
        // private static baseCreate (prototype) {
        //     if (!Underscore.isObject(prototype)) return {};
        //     if (nativeCreate) return nativeCreate(prototype);
        //     Ctor.prototype = prototype;
        //     var result = new Ctor;
        //     Ctor.prototype = null;
        //     return result;
        // };

        private static property (key) {
            return function(obj) {
                return obj == null ? void 0 : obj[key];
            };
        };

        // Helper for collection methods to determine whether a collection
        // should be iterated as an array or as an object
        // Related: http://people.mozilla.org/~jorendorff/es6-draft.html#sec-tolength
        // Avoids a very nasty iOS 8 JIT bug on ARM-64. #2094
        private static MAXUnderscoreARRAYUnderscoreINDEX = Math.pow(2, 53) - 1;
        private static getLength = Underscore.property('length');
        private static isArrayLike (collection) {
            var length = Underscore.getLength(collection);
            return typeof length == 'number' && length >= 0 && length <= Underscore.MAXUnderscoreARRAYUnderscoreINDEX;
        };

        // Generator function to create the findIndex and findLastIndex functions
        private static createPredicateIndexFinder(dir) {
            return function(array, predicate, context) {
                predicate = Underscore.cb(predicate, context);
                var length = Underscore.getLength(array);
                var index = dir > 0 ? 0 : length - 1;
                for (; index >= 0 && index < length; index += dir) {
                    if (predicate(array[index], index, array)) return index;
                }
                return -1;
            };
        }

        private static sortedIndex (array, obj, iteratee, context) {
            iteratee = Underscore.cb(iteratee, context, 1);
            var value = iteratee(obj);
            var low = 0, high = Underscore.getLength(array);
            while (low < high) {
                var mid = Math.floor((low + high) / 2);
                if (iteratee(array[mid]) < value) low = mid + 1; else high = mid;
            }
            return low;
        };

        private static findIndex = Underscore.createPredicateIndexFinder(1);
        private static findLastIndex = Underscore.createPredicateIndexFinder(-1);
        private static hasEnumBug = !{toString: null}.propertyIsEnumerable('toString');
        private static nonEnumerableProps = ['valueOf', 'isPrototypeOf', 'toString', 'propertyIsEnumerable', 'hasOwnProperty', 'toLocaleString'];
        private static indexOf = Underscore.createIndexFinder(1, Underscore.findIndex, Underscore.sortedIndex);
        private static isNaN (obj) {
            return Underscore.isNumber(obj) && obj !== +obj;
        };

        private static isNumber (obj) {
            return toString.call(obj) === '[object Number]';
        };

        private static values (obj) {
            var keys = Underscore.keys(obj);
            var length = keys.length;
            var values = Array(length);
            for (var i = 0; i < length; i++) {
                values[i] = obj[keys[i]];
            }
            return values;
        };

        private static createIndexFinder(dir, predicateFind, sortedIndex) {
            return function(array, item, idx) {
                var i = 0, length = Underscore.getLength(array);
                if (typeof idx == 'number') {
                    if (dir > 0) {
                        i = idx >= 0 ? idx : Math.max(idx + length, i);
                    } else {
                        length = idx >= 0 ? Math.min(idx + 1, length) : idx + length + 1;
                    }
                } else if (sortedIndex && idx && length) {
                    idx = sortedIndex(array, item);
                    return array[idx] === item ? idx : -1;
                }
                if (item !== item) {
                    idx = predicateFind(Underscore.slice.call(array, i, length), Underscore.isNaN);
                    return idx >= 0 ? idx + i : -1;
                }
                for (idx = dir > 0 ? i : length - 1; idx >= 0 && idx < length; idx += dir) {
                    if (array[idx] === item) return idx;
                }
                return -1;
            };
        }

        private static contains (obj, item, fromIndex = null, guard = null) {
            if (!Underscore.isArrayLike(obj)) obj = Underscore.values(obj);
            if (typeof fromIndex != 'number' || guard) fromIndex = 0;
            return Underscore.indexOf(obj, item, fromIndex) >= 0;
        };

        private static collectNonEnumProps(obj, keys) {
            var nonEnumIdx = Underscore.nonEnumerableProps.length;
            var constructor = obj.constructor;
            var proto = (Underscore.isFunction(constructor) && constructor.prototype) || Underscore.ObjProto;

            // Constructor is a special case.
            var prop = 'constructor';
            if (Underscore.has(obj, prop) && !Underscore.contains(keys, prop)) keys.push(prop);

            while (nonEnumIdx--) {
                prop = Underscore.nonEnumerableProps[nonEnumIdx];
                if (prop in obj && obj[prop] !== proto[prop] && !Underscore.contains(keys, prop)) {
                    keys.push(prop);
                }
            }
        }

        public static has = function(obj, key) {
            return obj != null && Underscore.hasOwnProperty.call(obj, key);
        };

        public static keys (obj) {
            if (!Underscore.isObject(obj)) return [];
            if (Underscore.nativeKeys) return Underscore.nativeKeys(obj);
            var keys = [];
            for (var key in obj) if (Underscore.has(obj, key)) keys.push(key);
            // Ahem, IE < 9.
            if (Underscore.hasEnumBug) Underscore.collectNonEnumProps(obj, keys);
            return keys;
        };

        // Collection Functions
        // --------------------

        // The cornerstone, an `each` implementation, aka `forEach`.
        // Handles raw objects in addition to array-likes. Treats all
        // sparse array-likes as if they were dense.
        public static each (obj, iteratee, context) {
            iteratee = Underscore.optimizeCb(iteratee, context);
            var i, length;
            if (Underscore.isArrayLike(obj)) {
                for (i = 0, length = obj.length; i < length; i++) {
                    iteratee(obj[i], i, obj);
                }
            } else {
                var keys = Underscore.keys(obj);
                for (i = 0, length = keys.length; i < length; i++) {
                    iteratee(obj[keys[i]], keys[i], obj);
                }
            }
            return obj;
        };

        // Return the results of applying the iteratee to each element.
        public static map (obj, iteratee, context = null) {
            iteratee = Underscore.cb(iteratee, context);
            var keys = !Underscore.isArrayLike(obj) && Underscore.keys(obj),
                length = (keys || obj).length,
                results = Array(length);
            for (var index = 0; index < length; index++) {
                var currentKey = keys ? keys[index] : index;
                results[index] = iteratee(obj[currentKey], currentKey, obj);
            }
            return results;
        };

        // Returns everything but the first entry of the array. Aliased as `tail` and `drop`.
        // Especially useful on the arguments object. Passing an **n** will return
        // the rest N values in the array.
        public static rest (array, n, guard = null) {
            return Underscore.slice.call(array, n == null || guard ? 1 : n);
        };

        public static allKeys (obj) {
            if (!Underscore.isObject(obj)) return [];
            var keys = [];
            for (var key in obj) keys.push(key);
            // Ahem, IE < 9.
            if (Underscore.hasEnumBug) Underscore.collectNonEnumProps(obj, keys);
            return keys;
        };

    }
}


// WEBPACK FOOTER //
// src/typescript/Core/Underscore/underscore.ts